<script setup>
import { computed } from 'vue'
import { useNow } from '@/composables/useNow'
import { countPlayableVideos, isScheduleActive, playableRuntimeMs } from '@/Utils/scheduledPreviewState'
import IconBase from '@/components/icon/IconBase.vue'
import IconCheckStroke from '@/components/icon/IconCheckStroke.vue'
import IconChevronRightSmall from '@/components/icon/IconChevronRightSmall.vue'

// Guided setup + at-a-glance summary for the pre-recorded stream page.
// Shown while the stream isn't live (where encoder stat chips would be empty),
// so the top of the page is always informative and nudges the 3 setup steps.
const props = defineProps({
	videoFiles: { type: Array, default: () => [] },
	videoFilesLoaded: { type: Boolean, default: true },
	platforms: { type: Array, default: () => [] },
	schedulerSettings: { type: Object, default: () => ({}) },
	enabled: { type: Boolean, default: true },
	// The preview pane's state; a run it reports as over no longer counts as scheduled.
	previewState: { type: String, default: null },
})

const now = useNow(5000)

// Same rules as the preview pane (scheduledPreviewState): only enabled videos
// play, and a date & time whose run is over no longer counts as scheduled.
const hasVideos = computed(() => countPlayableVideos(props.videoFiles) > 0)
const hasDestinations = computed(() => (props.platforms || []).length > 0)
const scheduleMode = computed(() => props.schedulerSettings?.mode || null)
const hasSchedule = computed(() =>
	isScheduleActive(props.schedulerSettings, now.value, playableRuntimeMs(props.videoFiles)) &&
	!['ended', 'missed'].includes(props.previewState),
)
const hasPastSchedule = computed(() =>
	scheduleMode.value === 'datetime' && !!props.schedulerSettings?.datetime && !hasSchedule.value,
)

const videosStepDone = computed(() => props.videoFilesLoaded && hasVideos.value)
const videosStepLoading = computed(() => !props.videoFilesLoaded)

const steps = computed(() => [
	{ n: 1, label: 'Add videos', done: videosStepDone.value, loading: videosStepLoading.value },
	{ n: 2, label: 'Add destination', done: hasDestinations.value, loading: false },
	{ n: 3, label: 'Schedule', done: hasSchedule.value, loading: false },
])
const allDone = computed(() => props.enabled && steps.value.every((s) => s.done))

const totalSeconds = computed(() =>
	(props.videoFiles || []).reduce(
		(sum, v) => sum + (v.enabled && v.mediaInfo && v.mediaInfo.duration ? v.mediaInfo.duration : 0),
		0,
	),
)
const durationText = computed(() => {
	const s = totalSeconds.value
	if (s <= 0) return ''
	const h = Math.floor(s / 3600)
	const m = Math.floor((s % 3600) / 60)
	const sec = Math.floor(s % 60)
	return h > 0 ? `${h}h ${m}m` : `${m}m ${sec}s`
})
const scheduleText = computed(() => {
	if (scheduleMode.value === 'loop') return 'Loop'
	if ((hasSchedule.value || hasPastSchedule.value) && props.schedulerSettings?.datetime) {
		const d = new Date(props.schedulerSettings.datetime)
		const hh = d.getHours()
		const mm = d.getMinutes().toString().padStart(2, '0')
		const ampm = hh >= 12 ? 'PM' : 'AM'
		return `${hasPastSchedule.value ? 'Last scheduled' : 'Scheduled'} ${hh % 12 || 12}:${mm} ${ampm}`
	}
	return ''
})
const summary = computed(() => {
	if (videosStepLoading.value) return 'Loading playlist…'
	const vids = (props.videoFiles || []).length
	const parts = [`${vids} ${vids === 1 ? 'video' : 'videos'}`]
	if (durationText.value) parts.push(durationText.value)
	if (scheduleText.value) parts.push(scheduleText.value)
	return parts.join(' · ')
})
</script>

<template>
	<div class="bg-surface-2 rounded-xl border border-surface-3 px-3 py-2.5 md:px-4 flex flex-wrap items-center gap-x-3 gap-y-2">
		<!-- Guided steps -->
		<div class="flex items-center gap-x-1.5 flex-wrap grow min-w-0">
			<template v-for="(s, i) in steps" :key="s.n">
				<span class="inline-flex items-center gap-1.5 text-sm"
					:class="s.loading ? 'text-surface-8' : (s.done ? 'text-surface-text-1' : 'text-surface-7')">
					<span class="flex size-5 items-center justify-center rounded-full text-[11px] font-semibold shrink-0"
						:class="s.loading ? 'bg-surface-3 animate-pulse' : (s.done ? 'bg-success text-white' : 'bg-surface-3 text-surface-7')">
						<icon-base v-if="s.done && !s.loading" fill="none" width="11" height="11" width-view-box="24" height-view-box="24">
							<icon-check-stroke />
						</icon-base>
						<template v-else-if="!s.loading">{{ s.n }}</template>
					</span>
					<span class="font-medium whitespace-nowrap">{{ s.label }}</span>
				</span>
				<icon-base v-if="i < steps.length - 1" class="text-surface-6 shrink-0" fill="none" width="14" height="14" width-view-box="24" height-view-box="24">
					<icon-chevron-right-small />
				</icon-base>
			</template>
		</div>
		<!-- Summary / ready state -->
		<div class="text-tiny shrink-0 tabular-nums">
			<span v-if="allDone" class="inline-flex items-center gap-1.5 text-success font-medium">
				<span class="size-1.5 rounded-full bg-success"></span> Ready to go live
			</span>
			<span v-else class="text-surface-8" :class="{ 'animate-pulse': videosStepLoading }">{{ summary }}</span>
		</div>
	</div>
</template>
